import React, { useEffect, useState } from 'react';
import { getCurrentUser, getCookie } from '../../functions/utils';
import DropdownComponent from './DropdownComponent';

function buildTree(classifications, mappings) {
  const nodeById = {};
  const nodeByCode = {};

  for (const cl of classifications) {
    const node = {
      id: cl.ID,
      code: cl.Code,
      description: cl.Description,
      parentId: cl.Parent,
      periodType: cl.PeriodType,
      children: [],
      accounts: [],
    };
    nodeById[cl.ID] = node;
    nodeByCode[cl.Code] = node;
  }

  const roots = [];
  for (const node of Object.values(nodeById)) {
    if (node.parentId && nodeById[node.parentId]) {
      nodeById[node.parentId].children.push(node);
    } else {
      roots.push(node);
    }
  }

  for (const m of mappings) {
    const node = nodeByCode[m.ClassificationCode];
    if (node) {
      node.accounts.push({
        code: m.GLAccountCode,
        description: m.GLAccountDescription,
      });
    }
  }

  return roots;
}

const TreeNode = ({ node, depth = 0 }) => {
  const [collapsed, setCollapsed] = useState(depth > 1);
  const hasChildren = node.children.length > 0 || node.accounts.length > 0;

  return (
    <div style={{ marginLeft: depth * 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '3px 0',
          cursor: hasChildren ? 'pointer' : 'default',
          fontWeight: depth < 2 ? 'bold' : 'normal',
          fontSize: depth === 0 ? '15px' : '13px',
        }}
        onClick={() => hasChildren && setCollapsed(!collapsed)}
      >
        {hasChildren && (
          <span style={{ width: '16px', display: 'inline-block', fontSize: '10px', color: '#666' }}>
            {collapsed ? '▶' : '▼'}
          </span>
        )}
        {!hasChildren && <span style={{ width: '16px', display: 'inline-block' }} />}
        <span style={{ color: '#555', marginRight: '8px', fontFamily: 'monospace' }}>{node.code}</span>
        <span>{node.description}</span>
        {node.accounts.length > 0 && (
          <span style={{ color: '#888', marginLeft: '8px', fontSize: '12px' }}>
            ({node.accounts.length} account{node.accounts.length !== 1 ? 's' : ''})
          </span>
        )}
      </div>
      {!collapsed && (
        <>
          {node.accounts.length > 0 && (
            <div style={{ marginLeft: (depth + 1) * 20, marginBottom: '4px' }}>
              {node.accounts.map((acc, i) => (
                <div key={i} style={{ padding: '2px 0', fontSize: '12px', color: '#2a7ae2', display: 'flex', gap: '8px' }}>
                  <span style={{ fontFamily: 'monospace', minWidth: '60px' }}>{acc.code}</span>
                  <span>{acc.description}</span>
                </div>
              ))}
            </div>
          )}
          {node.children
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((child) => (
              <TreeNode key={child.id} node={child} depth={depth + 1} />
            ))}
        </>
      )}
    </div>
  );
};

const TestEnvironment = () => {
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState('');
  const [glSchemeCode, setGlSchemeCode] = useState('RGS versie 3.2');
  const [taxonomyNamespace, setTaxonomyNamespace] = useState('RGS');
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDivisions();
  }, []);

  async function fetchDivisions() {
    try {
      const currentUser = await getCurrentUser();
      const response = await fetch('/exactonline/divisions?divisionId=' + getCookie('current_division') + '&currentUser=' + currentUser);
      if (response.ok) {
        const data = await response.json();
        setDivisions(data);
      }
    } catch (err) {
      console.error('Error fetching divisions:', err);
    }
  }

  function handleSelect(selectedOption) {
    if (selectedOption.type === 'administration') {
      setDivisionId(selectedOption.value);
    }
  }

  async function fetchMappingStructure() {
    setLoading(true);
    setError(null);
    setTree(null);
    setStats(null);
    try {
      const currentUser = await getCurrentUser();
      const division = divisionId || getCookie('current_division');

      const classParams = new URLSearchParams({ divisionId: division, currentUser });
      if (taxonomyNamespace) classParams.append('taxonomyNamespace', taxonomyNamespace);

      const rgsParams = new URLSearchParams({ divisionId: division, currentUser });
      if (glSchemeCode) rgsParams.append('glSchemeCode', glSchemeCode);

      const [classRes, rgsRes] = await Promise.all([
        fetch('/exactonline/glclassifications?' + classParams.toString()),
        fetch('/exactonline/rgs?' + rgsParams.toString()),
      ]);

      if (!classRes.ok) throw new Error('GLClassifications: HTTP ' + classRes.status);
      if (!rgsRes.ok) throw new Error('RGS Mappings: HTTP ' + rgsRes.status);

      const classData = await classRes.json();
      const rgsData = await rgsRes.json();

      const classifications = classData.results || [];
      const mappings = rgsData.results || [];

      const roots = buildTree(classifications, mappings);
      setTree(roots);
      setStats({ classifications: classifications.length, mappings: mappings.length });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ padding: '20px' }}>
      <h3>RGS Mapping Structure</h3>
      <div style={{ marginBottom: '10px' }}>
        <p>Administration</p>
        <DropdownComponent
          options={divisions.map((division) => ({ label: division.Description, value: division.Code, type: 'administration' }))}
          defaultValue={{ label: 'Select Administration', value: null, type: null }}
          onSelect={handleSelect}
          className="administration"
        />
      </div>
      <div style={{ display: 'flex', gap: '15px', marginBottom: '10px' }}>
        <div>
          <label>GL Scheme Code: </label>
          <input
            value={glSchemeCode}
            onChange={(e) => setGlSchemeCode(e.target.value)}
            style={{ marginLeft: '5px', width: '180px' }}
          />
        </div>
        <div>
          <label>Taxonomy Namespace: </label>
          <input
            value={taxonomyNamespace}
            onChange={(e) => setTaxonomyNamespace(e.target.value)}
            style={{ marginLeft: '5px', width: '180px' }}
          />
        </div>
      </div>
      <button className="import-btn hover" onClick={fetchMappingStructure} disabled={loading}>
        {loading ? 'Loading...' : 'Fetch Mapping Structure'}
      </button>

      {error && <p style={{ color: 'red' }}>Error: {error}</p>}

      {stats && (
        <p style={{ margin: '10px 0', fontSize: '13px', color: '#666' }}>
          {stats.classifications} classifications, {stats.mappings} GL account mappings
        </p>
      )}

      {tree && (
        <div style={{ marginTop: '10px', border: '1px solid #ddd', borderRadius: '4px', padding: '12px', maxHeight: '70vh', overflow: 'auto' }}>
          {tree
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((root) => (
              <TreeNode key={root.id} node={root} depth={0} />
            ))}
        </div>
      )}
    </div>
  );
};

export default TestEnvironment;
